import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

/* ============================ API helpers ============================ */
const API = process.env.REACT_APP_API_BASE ?? "";

function withAuth(opts = {}) {
  const token = localStorage.getItem("token") || "";
  return {
    credentials: "include",
    ...opts,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  };
}

async function j(url, opts) {
  const res = await fetch(url, withAuth(opts));
  const ct = res.headers.get("content-type") || "";
  const data = ct.includes("application/json") ? await res.json() : null;

  if (!res.ok || data?.ok === false) {
    const msg =
      data?.error || data?.message || data?.details || `HTTP ${res.status} ${url}`;
    throw new Error(msg);
  }

  return data;
}

async function jSoft(url, opts) {
  try {
    return await j(url, opts);
  } catch {
    return null;
  }
}

/* ============================ UI classes ============================ */
const BTN =
  "px-3 py-2 rounded-xl bg-gradient-to-br from-[#6c56b5] to-[#2c204f] text-white border border-white/20 hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed";
const BTN_LITE =
  "px-3 py-2 rounded-xl bg-white/10 border border-white/20 hover:bg-white/15 text-white disabled:opacity-60 disabled:cursor-not-allowed";
const BTN_DANGER =
  "px-2 py-1 rounded-lg bg-rose-600/70 hover:bg-rose-600 text-white disabled:opacity-60 disabled:cursor-not-allowed";
const INP =
  "w-full px-3 py-2 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-400/60";
const LABEL = "text-sm text-white/80";
const CARD = "bg-white/6 border border-white/10 rounded-2xl p-4";
const TAB_ACTIVE =
  "px-3 py-2 rounded-xl bg-gradient-to-br from-[#6c56b5] to-[#2c204f] text-white border border-white/20";
const TAB =
  "px-3 py-2 rounded-xl bg-white/10 border border-white/20 hover:bg-white/15 text-white";

const SEAT_TYPE_OPTIONS = ["standard", "balcony", "vip", "стандарт", "балкон", "промо", "нм 5", "нм 5.50"];
const CATEGORY_OPTIONS = ["normal", "child", "student", "senior", "discount"];

/* ============================ Normalizers ============================ */
function normalizeCinemas(raw) {
  const arr = Array.isArray(raw) ? raw : raw?.rows || raw?.data || [];
  return (arr || [])
    .map((c) => ({
      cinema_id: String(c.cinema_id ?? c.CINEMA_ID ?? c.id ?? ""),
      cinema_name: c.cinema_name ?? c.CINEMA_NAME ?? c.name ?? "Cinema",
    }))
    .filter((c) => c.cinema_id);
}

function normalizeMovies(raw) {
  const arr = Array.isArray(raw) ? raw : raw?.rows || raw?.data || [];
  return (arr || [])
    .map((m) => ({
      movie_id: Number(m.movie_id ?? m.MOVIE_ID ?? m.id),
      movie_name: m.movie_name ?? m.MOVIE_NAME ?? m.title ?? m.name ?? "Untitled",
    }))
    .filter((m) => Number.isFinite(m.movie_id));
}

function normalizeHalls(raw) {
  const arr = Array.isArray(raw) ? raw : raw?.rows || raw?.data || [];
  return (arr || [])
    .map((h) => ({
      hall_id: String(h.hall_id ?? h.HALL_ID ?? h.id ?? ""),
      hall_name:
        h.hall_name ??
        h.HALL_NAME ??
        h.name ??
        `Hall #${h.hall_id ?? h.HALL_ID ?? h.id ?? ""}`,
    }))
    .filter((h) => h.hall_id);
}

function normalizeSeatPricingRows(raw) {
  const arr = Array.isArray(raw) ? raw : raw?.rows || raw?.data || [];
  return (arr || []).map((r) => ({
    id: Number(r.id ?? r.ID ?? 0),
    cinema_id: String(r.cinema_id ?? r.CINEMA_ID ?? ""),
    hall_id: String(r.hall_id ?? r.HALL_ID ?? ""),
    seat_type: String(r.seat_type ?? r.SEAT_TYPE ?? "").toLowerCase(),
    category: String(r.category ?? r.CATEGORY ?? "normal").toLowerCase(),
    price: Number(r.price ?? r.PRICE ?? 0),
  }));
}

function normalizeMovieOverrideRows(raw) {
  const arr = Array.isArray(raw) ? raw : raw?.rows || raw?.data || [];
  return (arr || []).map((r) => ({
    cinema_id: String(r.cinema_id ?? r.CINEMA_ID ?? ""),
    movie_id: Number(r.movie_id ?? r.MOVIE_ID ?? 0),
    movie_name: r.movie_name ?? r.MOVIE_NAME ?? "",
    base_price: Number(r.base_price ?? r.BASE_PRICE ?? 0),
    balcony_extra: Number(r.balcony_extra ?? r.BALCONY_EXTRA ?? 0),
    vip_extra: Number(r.vip_extra ?? r.VIP_EXTRA ?? 0),
  }));
}

function money(v) {
  return Number(v || 0).toFixed(2);
}

export default function AdminMoviePricing() {
  const navigate = useNavigate();

  const [mode, setMode] = useState("movie"); // movie | cinema | hall

  const [cinemas, setCinemas] = useState([]);
  const [movies, setMovies] = useState([]);
  const [halls, setHalls] = useState([]);

  const [cinemaId, setCinemaId] = useState("");
  const [hallId, setHallId] = useState("");

  const [movieRows, setMovieRows] = useState([]);
  const [seatRows, setSeatRows] = useState([]);
  const [hallRows, setHallRows] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Shared search
  const [q, setQ] = useState("");

  // Movie override form
  const [movieId, setMovieId] = useState("");
  const [basePrice, setBasePrice] = useState("");
  const [balconyExtra, setBalconyExtra] = useState("");
  const [vipExtra, setVipExtra] = useState("");

  // Seat / hall pricing form
  const [seatType, setSeatType] = useState("standard");
  const [category, setCategory] = useState("normal");
  const [seatPrice, setSeatPrice] = useState("");

  const cinemaName = useMemo(
    () => cinemas.find((c) => c.cinema_id === String(cinemaId))?.cinema_name || "",
    [cinemas, cinemaId]
  );

  const hallName = useMemo(
    () => halls.find((h) => h.hall_id === String(hallId))?.hall_name || "",
    [halls, hallId]
  );

  const movieMap = useMemo(() => {
    const m = new Map();
    for (const mm of movies) m.set(Number(mm.movie_id), mm.movie_name);
    return m;
  }, [movies]);

  const filteredMovieRows = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return movieRows;
    return movieRows.filter((r) => {
      const name = String(r.movie_name ?? movieMap.get(Number(r.movie_id)) ?? "").toLowerCase();
      return name.includes(qq) || String(r.movie_id).includes(qq);
    });
  }, [movieRows, q, movieMap]);

  const filteredSeatRows = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return seatRows;
    return seatRows.filter((r) => {
      return (
        String(r.seat_type).toLowerCase().includes(qq) ||
        String(r.category).toLowerCase().includes(qq) ||
        String(r.price).includes(qq)
      );
    });
  }, [seatRows, q]);

  const filteredHallRows = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return hallRows;
    return hallRows.filter((r) => {
      return (
        String(r.seat_type).toLowerCase().includes(qq) ||
        String(r.category).toLowerCase().includes(qq) ||
        String(r.price).includes(qq) ||
        String(r.hall_id).includes(qq)
      );
    });
  }, [hallRows, q]);

  function clearMessages() {
    setError("");
    setSuccess("");
  }

  function clearMovieForm() {
    setMovieId("");
    setBasePrice("");
    setBalconyExtra("");
    setVipExtra("");
    clearMessages();
  }

  function clearSeatForm() {
    setSeatType("standard");
    setCategory("normal");
    setSeatPrice("");
    clearMessages();
  }

  /* ============================ Data loaders ============================ */
  async function loadMovieOverrides(cid) {
    if (!cid) return;
    try {
      const data = await j(`${API}/api/movie-cinema-pricing/list?cinema_id=${encodeURIComponent(cid)}`);
      setMovieRows(normalizeMovieOverrideRows(data));
    } catch (e) {
      setMovieRows([]);
      throw e;
    }
  }

  async function loadSeatPricing(cid) {
    if (!cid) return;
    try {
      const data = await j(`${API}/api/seat-pricing/${encodeURIComponent(cid)}/base`);
      setSeatRows(normalizeSeatPricingRows(data));
    } catch (e) {
      setSeatRows([]);
      throw e;
    }
  }

  async function loadHallPricing(cid, hid) {
    if (!cid || !hid) {
      setHallRows([]);
      return;
    }
    try {
      const data = await j(
        `${API}/api/seat-pricing/${encodeURIComponent(cid)}/hall/${encodeURIComponent(hid)}`
      );
      setHallRows(normalizeSeatPricingRows(data));
    } catch (e) {
      setHallRows([]);
      throw e;
    }
  }

  async function loadHalls(cid) {
    if (!cid) return;

    try {
      const h1 = await jSoft(`${API}/api/halls?cinema_id=${encodeURIComponent(cid)}`);
      const h2 = h1 ? h1 : await jSoft(`${API}/api/cinema-halls?cinema_id=${encodeURIComponent(cid)}`);
      const hs = normalizeHalls(h2 || []);
      setHalls(hs);

      const nextHallId = hs[0]?.hall_id ? String(hs[0].hall_id) : "";
      setHallId((prev) => {
        const exists = hs.some((h) => String(h.hall_id) === String(prev));
        return exists ? prev : nextHallId;
      });
    } catch {
      setHalls([]);
      setHallId("");
      setHallRows([]);
    }
  }

  async function loadBoot() {
    setLoading(true);
    setError("");

    try {
      const c1 = await jSoft(`${API}/api/fiscal-devices/cinemas`);
      const c2 = c1 ? c1 : await j(`${API}/api/cinemas`);
      const cs = normalizeCinemas(c2);
      setCinemas(cs);

      const firstCinemaId = cs[0]?.cinema_id ? String(cs[0].cinema_id) : "";
      const chosenCinemaId = String(cinemaId || firstCinemaId || "");
      if (!cinemaId && chosenCinemaId) setCinemaId(chosenCinemaId);

      const ms = normalizeMovies(await j(`${API}/api/movies`));
      setMovies(ms);

      if (chosenCinemaId) {
        await Promise.all([
          loadMovieOverrides(chosenCinemaId),
          loadSeatPricing(chosenCinemaId),
          loadHalls(chosenCinemaId),
        ]);
      }
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }

  async function refreshCurrentMode() {
    clearMessages();
    try {
      if (!cinemaId) return;

      if (mode === "movie") {
        await loadMovieOverrides(cinemaId);
      } else if (mode === "cinema") {
        await loadSeatPricing(cinemaId);
      } else if (mode === "hall") {
        await loadHalls(cinemaId);
        if (hallId) await loadHallPricing(cinemaId, hallId);
      }
    } catch (e) {
      setError(String(e.message || e));
    }
  }

  /* ============================ Effects ============================ */
  useEffect(() => {
    loadBoot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!cinemaId) return;

    (async () => {
      clearMessages();
      try {
        await Promise.all([
          loadMovieOverrides(cinemaId),
          loadSeatPricing(cinemaId),
          loadHalls(cinemaId),
        ]);
      } catch (e) {
        setError(String(e.message || e));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cinemaId]);

  useEffect(() => {
    if (!cinemaId || !hallId) {
      setHallRows([]);
      return;
    }

    (async () => {
      try {
        await loadHallPricing(cinemaId, hallId);
      } catch (e) {
        setError(String(e.message || e));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cinemaId, hallId]);

  /* ============================ Pick row helpers ============================ */
  function pickMovieRow(r) {
    setMode("movie");
    setMovieId(String(r.movie_id ?? ""));
    setBasePrice(String(r.base_price ?? ""));
    setBalconyExtra(String(r.balcony_extra ?? ""));
    setVipExtra(String(r.vip_extra ?? ""));
    clearMessages();
  }

  function pickSeatRow(r) {
    setMode("cinema");
    setSeatType(String(r.seat_type ?? "standard"));
    setCategory(String(r.category ?? "normal"));
    setSeatPrice(String(r.price ?? ""));
    clearMessages();
  }

  function pickHallRow(r) {
    setMode("hall");
    if (r.hall_id) setHallId(String(r.hall_id));
    setSeatType(String(r.seat_type ?? "standard"));
    setCategory(String(r.category ?? "normal"));
    setSeatPrice(String(r.price ?? ""));
    clearMessages();
  }

  /* ============================ Save / Delete ============================ */
  async function saveMovieOverride() {
    setSaving(true);
    clearMessages();

    try {
      const cid = Number(cinemaId);
      const mid = Number(movieId);

      if (!cid || !mid) throw new Error("Изберете кино и филм");

      await j(`${API}/api/movie-cinema-pricing/upsert`, {
        method: "POST",
        body: JSON.stringify({
          cinema_id: cid,
          movie_id: mid,
          base_price: Number(basePrice || 0),
          balcony_extra: Number(balconyExtra || 0),
          vip_extra: Number(vipExtra || 0),
        }),
      });

      setSuccess("✅ Movie override saved");
      await loadMovieOverrides(cinemaId);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setSaving(false);
    }
  }

  async function deleteMovieOverride(r) {
    const cid = cinemaId;
    const mid = r.movie_id;

    if (!cid || !mid) return;
    if (!window.confirm(`Delete movie override for movie #${mid}?`)) return;

    clearMessages();

    try {
      await j(
        `${API}/api/movie-cinema-pricing/delete?cinema_id=${encodeURIComponent(
          cid
        )}&movie_id=${encodeURIComponent(mid)}`,
        { method: "DELETE" }
      );

      setSuccess("🗑️ Movie override deleted");
      await loadMovieOverrides(cinemaId);

      if (String(movieId) === String(mid)) clearMovieForm();
    } catch (e) {
      setError(String(e.message || e));
    }
  }

  async function saveSeatPricing() {
    setSaving(true);
    clearMessages();

    try {
      const cid = Number(cinemaId);
      if (!cid) throw new Error("Изберете кино");

      const nextRows = [...seatRows];
      const idx = nextRows.findIndex(
        (r) =>
          String(r.seat_type).toLowerCase() === String(seatType).toLowerCase() &&
          String(r.category).toLowerCase() === String(category).toLowerCase()
      );

      const row = {
        seat_type: seatType,
        category,
        price: Number(seatPrice || 0),
      };

      if (idx >= 0) nextRows[idx] = { ...nextRows[idx], ...row };
      else nextRows.push(row);

      await j(`${API}/api/seat-pricing/${cid}`, {
        method: "POST",
        body: JSON.stringify(nextRows.map((r) => ({
          seat_type: r.seat_type,
          category: r.category,
          price: Number(r.price || 0),
        }))),
      });

      setSuccess("✅ Standard cinema pricing saved");
      await loadSeatPricing(cinemaId);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setSaving(false);
    }
  }

  async function deleteSeatPricing(r) {
    if (!cinemaId) return;

    if (
      !window.confirm(
        `Delete cinema seat pricing for ${r.seat_type} / ${r.category}?`
      )
    ) {
      return;
    }

    clearMessages();

    try {
      const nextRows = seatRows.filter(
        (x) =>
          !(
            String(x.seat_type).toLowerCase() === String(r.seat_type).toLowerCase() &&
            String(x.category).toLowerCase() === String(r.category).toLowerCase()
          )
      );

      await j(`${API}/api/seat-pricing/${encodeURIComponent(cinemaId)}`, {
        method: "POST",
        body: JSON.stringify(nextRows.map((x) => ({
          seat_type: x.seat_type,
          category: x.category,
          price: Number(x.price || 0),
        }))),
      });

      setSuccess("🗑️ Standard cinema pricing deleted");
      await loadSeatPricing(cinemaId);

      if (
        String(seatType).toLowerCase() === String(r.seat_type).toLowerCase() &&
        String(category).toLowerCase() === String(r.category).toLowerCase()
      ) {
        clearSeatForm();
      }
    } catch (e) {
      setError(String(e.message || e));
    }
  }

  async function saveHallPricing() {
    setSaving(true);
    clearMessages();

    try {
      const cid = Number(cinemaId);
      const hid = Number(hallId);

      if (!cid || !hid) throw new Error("Изберете кино и зала");

      const existing = hallRows.find(
        (r) =>
          String(r.seat_type).toLowerCase() === String(seatType).toLowerCase() &&
          String(r.category).toLowerCase() === String(category).toLowerCase()
      );

      if (existing?.id) {
        await j(`${API}/api/seat-pricing/${cid}/hall/${hid}/${existing.id}`, {
          method: "PUT",
          body: JSON.stringify({
            seat_type: seatType,
            category,
            price: Number(seatPrice || 0),
          }),
        });
      } else {
        await j(`${API}/api/seat-pricing/${cid}/hall/${hid}`, {
          method: "POST",
          body: JSON.stringify({
            seat_type: seatType,
            category,
            price: Number(seatPrice || 0),
          }),
        });
      }

      setSuccess("✅ Hall pricing saved");
      await loadHallPricing(cinemaId, hallId);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setSaving(false);
    }
  }

  async function deleteHallPricing(r) {
    if (!cinemaId || !hallId) return;
    if (!r?.id) {
      setError("Missing hall pricing row id");
      return;
    }

    if (!window.confirm(`Delete hall pricing for ${r.seat_type} / ${r.category}?`)) {
      return;
    }

    clearMessages();

    try {
      await j(
        `${API}/api/seat-pricing/${encodeURIComponent(cinemaId)}/hall/${encodeURIComponent(
          hallId
        )}/${encodeURIComponent(r.id)}`,
        { method: "DELETE" }
      );

      setSuccess("🗑️ Hall pricing deleted");
      await loadHallPricing(cinemaId, hallId);

      if (
        String(seatType).toLowerCase() === String(r.seat_type).toLowerCase() &&
        String(category).toLowerCase() === String(r.category).toLowerCase()
      ) {
        clearSeatForm();
      }
    } catch (e) {
      setError(String(e.message || e));
    }
  }

  return (
    <div className="min-h-screen relative p-6">
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <img
          src="https://latonacinema.com/wp-content/uploads/2025/06/cinema-hall-with-rows-of-grey-seats-in-darkness.jpg"
          alt="фон"
          className="h-full w-full object-cover blur-[2px] scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#2c204f]/75 via-black/60 to-[#6c56b5]/65" />
      </div>

      <div className="max-w-7xl mx-auto bg-white/8 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl p-6 md:p-8">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-white">
              💶 Pricing Overrides
            </h1>
            <span className="text-sm text-white/60">(Admin only)</span>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button className={BTN_LITE} onClick={() => navigate("/admin/dashboard")}>
              ← Назад
            </button>
            <button className={BTN_LITE} onClick={refreshCurrentMode}>
              🔄 Обнови
            </button>
          </div>
        </div>

        <div className="mb-6 flex flex-wrap gap-2">
          <button
            className={mode === "movie" ? TAB_ACTIVE : TAB}
            onClick={() => {
              setMode("movie");
              clearMessages();
            }}
          >
            🎬 Movie specific
          </button>
          <button
            className={mode === "cinema" ? TAB_ACTIVE : TAB}
            onClick={() => {
              setMode("cinema");
              clearMessages();
            }}
          >
            🏢 Cinema standard
          </button>
          <button
            className={mode === "hall" ? TAB_ACTIVE : TAB}
            onClick={() => {
              setMode("hall");
              clearMessages();
            }}
          >
            🪑 Hall pricing
          </button>
        </div>

        {loading ? (
          <div className="text-white/80">Зареждане…</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className={mode === "hall" ? "md:col-span-1" : "md:col-span-2"}>
                <div className={LABEL}>Кино</div>
                <select
                  className={INP}
                  value={cinemaId}
                  onChange={(e) => setCinemaId(e.target.value)}
                >
                  {cinemas.map((c) => (
                    <option key={c.cinema_id} value={c.cinema_id} className="text-black">
                      {c.cinema_name}
                    </option>
                  ))}
                </select>
                <div className="text-xs text-white/50 mt-1">
                  Избрано кино: {cinemaName || "—"}
                </div>
              </div>

              {mode === "hall" && (
                <div className="md:col-span-1">
                  <div className={LABEL}>Зала</div>
                  <select
                    className={INP}
                    value={hallId}
                    onChange={(e) => setHallId(e.target.value)}
                  >
                    {!halls.length && (
                      <option value="" className="text-black">
                        — няма зали —
                      </option>
                    )}
                    {halls.map((h) => (
                      <option key={h.hall_id} value={h.hall_id} className="text-black">
                        {h.hall_name}
                      </option>
                    ))}
                  </select>
                  <div className="text-xs text-white/50 mt-1">
                    Избрана зала: {hallName || "—"}
                  </div>
                </div>
              )}

              <div className={mode === "hall" ? "md:col-span-2" : "md:col-span-2"}>
                <div className={LABEL}>Търсене</div>
                <input
                  className={INP}
                  placeholder={
                    mode === "movie"
                      ? "търси филм..."
                      : mode === "cinema"
                      ? "търси seat type / category..."
                      : "търси hall pricing..."
                  }
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
                <div className="text-xs text-white/50 mt-1">
                  {mode === "movie" && <>Намерени movie overrides: {filteredMovieRows.length}</>}
                  {mode === "cinema" && <>Намерени standard rows: {filteredSeatRows.length}</>}
                  {mode === "hall" && <>Намерени hall rows: {filteredHallRows.length}</>}
                </div>
              </div>
            </div>

            {(error || success) && (
              <div className="mb-4">
                {error && (
                  <div className="px-4 py-3 rounded-xl bg-rose-600/25 border border-rose-500/30 text-rose-100">
                    {error}
                  </div>
                )}
                {success && (
                  <div className="mt-2 px-4 py-3 rounded-xl bg-emerald-600/25 border border-emerald-500/30 text-emerald-100">
                    {success}
                  </div>
                )}
              </div>
            )}

            {mode === "movie" && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className={CARD}>
                  <div className="text-white font-semibold mb-3">
                    ➕ Add / Edit movie-specific pricing
                  </div>

                  <div className="space-y-3">
                    <div>
                      <div className={LABEL}>Филм</div>
                      <select
                        className={INP}
                        value={movieId}
                        onChange={(e) => setMovieId(e.target.value)}
                      >
                        <option value="" className="text-black">
                          — избери —
                        </option>
                        {movies
                          .slice()
                          .sort((a, b) => a.movie_name.localeCompare(b.movie_name))
                          .map((m) => (
                            <option
                              key={m.movie_id}
                              value={m.movie_id}
                              className="text-black"
                            >
                              {m.movie_name} (#{m.movie_id})
                            </option>
                          ))}
                      </select>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <div className={LABEL}>Base price</div>
                        <input
                          className={INP}
                          type="number"
                          step="0.01"
                          value={basePrice}
                          onChange={(e) => setBasePrice(e.target.value)}
                          placeholder="0"
                        />
                      </div>

                      <div>
                        <div className={LABEL}>Balcony extra</div>
                        <input
                          className={INP}
                          type="number"
                          step="0.01"
                          value={balconyExtra}
                          onChange={(e) => setBalconyExtra(e.target.value)}
                          placeholder="0"
                        />
                      </div>

                      <div>
                        <div className={LABEL}>VIP extra</div>
                        <input
                          className={INP}
                          type="number"
                          step="0.01"
                          value={vipExtra}
                          onChange={(e) => setVipExtra(e.target.value)}
                          placeholder="0"
                        />
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <button className={BTN} disabled={saving} onClick={saveMovieOverride}>
                        {saving ? "Запазване..." : "💾 Save movie override"}
                      </button>
                      <button className={BTN_LITE} onClick={clearMovieForm}>
                        ✖ Clear
                      </button>
                    </div>

                    <div className="text-xs text-white/55 leading-relaxed">
                      Priority:
                      <span className="font-semibold text-white/80">
                        {" "}
                        Movie specific → Hall pricing → Cinema standard
                      </span>
                    </div>
                  </div>
                </div>

                <div className={CARD}>
                  <div className="text-white font-semibold mb-3">
                    📋 Movie overrides for selected cinema
                  </div>

                  <div className="overflow-auto rounded-xl border border-white/10">
                    <table className="min-w-full text-sm">
                      <thead className="bg-white/5 text-white/80">
                        <tr>
                          <th className="px-3 py-2 text-left">Филм</th>
                          <th className="px-3 py-2 text-right">Base</th>
                          <th className="px-3 py-2 text-right">Balcony +</th>
                          <th className="px-3 py-2 text-right">VIP +</th>
                          <th className="px-3 py-2" />
                        </tr>
                      </thead>
                      <tbody className="text-white/90">
                        {filteredMovieRows.map((r) => (
                          <tr
                            key={`${r.cinema_id}-${r.movie_id}`}
                            className="border-t border-white/10 hover:bg-white/5"
                          >
                            <td className="px-3 py-2">
                              <div className="font-semibold">
                                {r.movie_name ??
                                  movieMap.get(Number(r.movie_id)) ??
                                  `#${r.movie_id}`}
                              </div>
                              <div className="text-xs text-white/50">
                                movie_id: {r.movie_id}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right">{money(r.base_price)}</td>
                            <td className="px-3 py-2 text-right">{money(r.balcony_extra)}</td>
                            <td className="px-3 py-2 text-right">{money(r.vip_extra)}</td>
                            <td className="px-3 py-2">
                              <div className="flex justify-end gap-2">
                                <button
                                  className="px-2 py-1 rounded-lg bg-white/10 border border-white/20 hover:bg-white/15"
                                  onClick={() => pickMovieRow(r)}
                                >
                                  Edit
                                </button>
                                <button
                                  className={BTN_DANGER}
                                  onClick={() => deleteMovieOverride(r)}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}

                        {!filteredMovieRows.length && (
                          <tr>
                            <td colSpan={5} className="px-3 py-4 text-center text-white/60">
                              No movie overrides for this cinema.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {mode === "cinema" && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className={CARD}>
                  <div className="text-white font-semibold mb-3">
                    ➕ Add / Edit standard cinema pricing
                  </div>

                  <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <div className={LABEL}>Seat type</div>
                        <input
                          className={INP}
                          value={seatType}
                          onChange={(e) => setSeatType(e.target.value)}
                          placeholder="standard / balcony / vip / промо..."
                          list="seat-type-options"
                        />
                        <datalist id="seat-type-options">
                          {SEAT_TYPE_OPTIONS.map((s) => (
                            <option key={s} value={s} />
                          ))}
                        </datalist>
                      </div>

                      <div>
                        <div className={LABEL}>Category</div>
                        <select
                          className={INP}
                          value={category}
                          onChange={(e) => setCategory(e.target.value)}
                        >
                          {CATEGORY_OPTIONS.map((c) => (
                            <option key={c} value={c} className="text-black">
                              {c}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <div className={LABEL}>Price</div>
                        <input
                          className={INP}
                          type="number"
                          step="0.01"
                          value={seatPrice}
                          onChange={(e) => setSeatPrice(e.target.value)}
                          placeholder="0"
                        />
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <button className={BTN} disabled={saving} onClick={saveSeatPricing}>
                        {saving ? "Запазване..." : "💾 Save standard pricing"}
                      </button>
                      <button className={BTN_LITE} onClick={clearSeatForm}>
                        ✖ Clear
                      </button>
                    </div>

                    <div className="text-xs text-white/55 leading-relaxed">
                      Standard cinema pricing is used when there is no hall pricing and no
                      movie-specific override.
                    </div>
                  </div>
                </div>

                <div className={CARD}>
                  <div className="text-white font-semibold mb-3">
                    📋 Standard cinema pricing rows
                  </div>

                  <div className="overflow-auto rounded-xl border border-white/10">
                    <table className="min-w-full text-sm">
                      <thead className="bg-white/5 text-white/80">
                        <tr>
                          <th className="px-3 py-2 text-left">Seat type</th>
                          <th className="px-3 py-2 text-left">Category</th>
                          <th className="px-3 py-2 text-right">Price</th>
                          <th className="px-3 py-2" />
                        </tr>
                      </thead>
                      <tbody className="text-white/90">
                        {filteredSeatRows.map((r) => (
                          <tr
                            key={`${r.cinema_id}-${r.seat_type}-${r.category}`}
                            className="border-t border-white/10 hover:bg-white/5"
                          >
                            <td className="px-3 py-2 font-semibold">{r.seat_type}</td>
                            <td className="px-3 py-2">{r.category}</td>
                            <td className="px-3 py-2 text-right">{money(r.price)}</td>
                            <td className="px-3 py-2">
                              <div className="flex justify-end gap-2">
                                <button
                                  className="px-2 py-1 rounded-lg bg-white/10 border border-white/20 hover:bg-white/15"
                                  onClick={() => pickSeatRow(r)}
                                >
                                  Edit
                                </button>
                                <button
                                  className={BTN_DANGER}
                                  onClick={() => deleteSeatPricing(r)}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}

                        {!filteredSeatRows.length && (
                          <tr>
                            <td colSpan={4} className="px-3 py-4 text-center text-white/60">
                              No standard cinema pricing rows.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {mode === "hall" && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className={CARD}>
                  <div className="text-white font-semibold mb-3">
                    ➕ Add / Edit hall pricing
                  </div>

                  <div className="space-y-3">
                    <div>
                      <div className={LABEL}>Hall</div>
                      <select
                        className={INP}
                        value={hallId}
                        onChange={(e) => setHallId(e.target.value)}
                      >
                        {!halls.length && (
                          <option value="" className="text-black">
                            — няма зали —
                          </option>
                        )}
                        {halls.map((h) => (
                          <option key={h.hall_id} value={h.hall_id} className="text-black">
                            {h.hall_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <div className={LABEL}>Seat type</div>
                        <input
                          className={INP}
                          value={seatType}
                          onChange={(e) => setSeatType(e.target.value)}
                          placeholder="standard / balcony / vip / промо..."
                          list="seat-type-options"
                        />
                        <datalist id="seat-type-options">
                          {SEAT_TYPE_OPTIONS.map((s) => (
                            <option key={s} value={s} />
                          ))}
                        </datalist>
                      </div>

                      <div>
                        <div className={LABEL}>Category</div>
                        <select
                          className={INP}
                          value={category}
                          onChange={(e) => setCategory(e.target.value)}
                        >
                          {CATEGORY_OPTIONS.map((c) => (
                            <option key={c} value={c} className="text-black">
                              {c}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <div className={LABEL}>Price</div>
                        <input
                          className={INP}
                          type="number"
                          step="0.01"
                          value={seatPrice}
                          onChange={(e) => setSeatPrice(e.target.value)}
                          placeholder="0"
                        />
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <button className={BTN} disabled={saving} onClick={saveHallPricing}>
                        {saving ? "Запазване..." : "💾 Save hall pricing"}
                      </button>
                      <button className={BTN_LITE} onClick={clearSeatForm}>
                        ✖ Clear
                      </button>
                    </div>

                    <div className="text-xs text-white/55 leading-relaxed">
                      Hall pricing overrides standard cinema pricing, but movie-specific
                      pricing still has highest priority.
                    </div>
                  </div>
                </div>

                <div className={CARD}>
                  <div className="text-white font-semibold mb-3">
                    📋 Hall pricing rows
                  </div>

                  <div className="overflow-auto rounded-xl border border-white/10">
                    <table className="min-w-full text-sm">
                      <thead className="bg-white/5 text-white/80">
                        <tr>
                          <th className="px-3 py-2 text-left">Seat type</th>
                          <th className="px-3 py-2 text-left">Category</th>
                          <th className="px-3 py-2 text-right">Price</th>
                          <th className="px-3 py-2" />
                        </tr>
                      </thead>
                      <tbody className="text-white/90">
                        {filteredHallRows.map((r) => (
                          <tr
                            key={`${r.id}-${r.cinema_id}-${r.hall_id}-${r.seat_type}-${r.category}`}
                            className="border-t border-white/10 hover:bg-white/5"
                          >
                            <td className="px-3 py-2 font-semibold">{r.seat_type}</td>
                            <td className="px-3 py-2">{r.category}</td>
                            <td className="px-3 py-2 text-right">{money(r.price)}</td>
                            <td className="px-3 py-2">
                              <div className="flex justify-end gap-2">
                                <button
                                  className="px-2 py-1 rounded-lg bg-white/10 border border-white/20 hover:bg-white/15"
                                  onClick={() => pickHallRow(r)}
                                >
                                  Edit
                                </button>
                                <button
                                  className={BTN_DANGER}
                                  onClick={() => deleteHallPricing(r)}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}

                        {!filteredHallRows.length && (
                          <tr>
                            <td colSpan={4} className="px-3 py-4 text-center text-white/60">
                              No hall pricing rows for this hall.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 text-xs text-white/50 leading-relaxed">
              Effective priority:
              <span className="font-semibold text-white/75">
                {" "}
                movie-specific pricing → hall pricing → cinema standard pricing
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}